// autoley_ui.jsx
// Shared presentational primitives for the AutoLey admin section, so the three
// managers (overview, licenses, activity) stay consistent. Mirrors
// qrcreate_ui.jsx with AutoLey's blue accent and the license-specific badges.
//
// ── Dataviz decisions, once, here ────────────────────────────────────────────
// Magnitude is length in ONE hue (blue-700 #1d4ed8, validated ≥3:1 on white) on
// a slate-100 track. Activations and refusals are different measures, so the
// overview draws them as two separate single-series charts — never one chart
// with two axes — and neither carries a legend: the section title names it.
//
// Status is the one categorical encoding: blocked (rose), likely duplicate
// (amber), possible duplicate / at limit / revoked (slate), active (emerald).
// Every badge carries an icon and its text, so state is never colour alone.
// Headline numbers are stat tiles, not one-bar charts.

import { useState } from 'react';
import { X, Loader2, Inbox, Ban, Copy, CircleSlash, CheckCircle2, Gauge } from 'lucide-react';

// The single magnitude hue.
export const MARK_HUE = '#1d4ed8';

// Modal — centered dialog with a header, scrollable body and optional footer.
export function Modal({ open, title, subtitle, onClose, children, footer, wide = false }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-slate-900/50 p-4 backdrop-blur-sm sm:items-center">
      <div className={`my-8 w-full ${wide ? 'max-w-5xl' : 'max-w-xl'} rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200`}>
        <div className="flex items-start justify-between border-b border-slate-100 px-6 py-4">
          <div className="min-w-0">
            <h3 className="truncate text-lg font-bold text-slate-800">{title}</h3>
            {subtitle && <p className="mt-0.5 truncate text-sm text-slate-500">{subtitle}</p>}
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="max-h-[75vh] overflow-y-auto px-6 py-5">{children}</div>
        {footer && <div className="flex justify-end gap-3 border-t border-slate-100 px-6 py-4">{footer}</div>}
      </div>
    </div>
  );
}

// Field — labelled form control wrapper.
export function Field({ label, hint, required, children, className = '' }) {
  return (
    <label className={`block ${className}`}>
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label} {required && <span className="text-rose-500">*</span>}
      </span>
      {children}
      {hint && <span className="mt-1 block text-xs text-slate-400">{hint}</span>}
    </label>
  );
}

// controlClass has no width, so a select can be given one (w-full and w-44 on
// one element is a coin toss in Tailwind's output order); inputClass fills.
export const controlClass =
  'rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200';
export const inputClass = `w-full ${controlClass}`;

export function Button({ variant = 'primary', className = '', children, ...rest }) {
  const variants = {
    primary: 'bg-blue-700 text-white hover:bg-blue-800 disabled:bg-blue-300',
    secondary: 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:text-slate-400',
    danger: 'bg-rose-600 text-white hover:bg-rose-700 disabled:bg-rose-300',
    ghost: 'text-slate-500 hover:bg-slate-100 hover:text-slate-700',
  };
  return (
    <button
      className={`inline-flex items-center justify-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold shadow-sm transition-colors disabled:cursor-not-allowed ${variants[variant]} ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}

export function Loading({ label = 'Loading…' }) {
  return (
    <div className="flex items-center justify-center gap-3 py-16 text-slate-500">
      <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
      <span className="text-sm font-medium">{label}</span>
    </div>
  );
}

export function EmptyState({ title = 'Nothing here yet', hint, icon: Icon = Inbox, action }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 px-4 py-14 text-center">
      <Icon className="mb-3 h-10 w-10 text-slate-300" />
      <p className="text-sm font-semibold text-slate-600">{title}</p>
      {hint && <p className="mt-1 max-w-md text-xs text-slate-400">{hint}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

// Section — titled card wrapping one report or table.
export function Section({ title, subtitle, icon: Icon, right, children, className = '' }) {
  return (
    <section className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm ${className}`}>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="h-4 w-4 text-blue-700" />}
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wide text-slate-700">{title}</h3>
            {subtitle && <p className="text-xs text-slate-400">{subtitle}</p>}
          </div>
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}

// ─── Formatting ──────────────────────────────────────────────────────────────

export const fmtInt = (n) => (n ?? 0).toLocaleString();

export const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
};

export const fmtDateTime = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

export const fmtDay = (day) => {
  const d = new Date(`${day}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? day : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
};

// "3 days ago", "in use 2 h ago" — how long since a timestamp, coarse on purpose.
export const fmtAgo = (v) => {
  if (!v) return '—';
  const ms = Date.now() - new Date(v).getTime();
  if (Number.isNaN(ms)) return '—';
  const min = Math.round(ms / 60000);
  if (min < 2) return 'just now';
  if (min < 60) return `${min} min ago`;
  const h = Math.round(min / 60);
  if (h < 36) return `${h} h ago`;
  const d = Math.round(h / 24);
  if (d < 60) return `${d} days ago`;
  return fmtDate(v);
};

// Gap between two timestamps, for "activated 3 h apart".
export const fmtGap = (a, b) => {
  const ms = Math.abs(new Date(a).getTime() - new Date(b).getTime());
  if (Number.isNaN(ms)) return '—';
  const min = Math.round(ms / 60000);
  if (min < 60) return `${min} min`;
  const h = Math.round(min / 60);
  if (h < 48) return `${h} h`;
  return `${Math.round(h / 24)} days`;
};

export const OS_LABELS = { windows: 'Windows', macos: 'macOS', linux: 'Linux' };
export const osLabel = (os) => OS_LABELS[os] || os || '—';

export const accountName = (a) => a?.name || a?.email || '(no name)';

// What each duplicate signal means, in the words support uses with the lawyer.
export const REASON_LABELS = {
  same_machine: 'Same machine ID',
  same_name: 'Same computer name',
  same_os: 'Same operating system',
  same_ip: 'Same IP at activation',
  went_quiet: 'Older record stopped checking in when the newer one appeared',
  activated_close: 'Activated within 3 days of each other',
};

// A machine id as shown: its first characters, or why there is none. Apps
// before machine ids didn't send one; such a computer reports it the next
// time it talks to the server.
export const machineLabel = (id) => (id ? `${id}…` : 'not reported yet');

// Who deactivated a device, from the AutoLey audit actor.
export const actorLabel = (actor) => {
  if (!actor) return '';
  if (actor.startsWith('device:')) return 'the computer itself';
  if (actor.startsWith('account:')) return 'the lawyer, on the website';
  if (actor.startsWith('admin:dashboard:')) return `support (${actor.split(':').slice(3).join(':') || 'dashboard'})`;
  if (actor.startsWith('admin:')) return `support CLI (${actor.slice(6)})`;
  if (actor === 'stripe') return 'Stripe';
  if (actor === 'anonymous') return 'an activation attempt';
  return actor;
};

// ─── Status encodings ────────────────────────────────────────────────────────

export function Badge({ children, className = '', icon: Icon, title }) {
  return (
    <span title={title} className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ${className}`}>
      {Icon && <Icon className="h-3 w-3" />}
      {children}
    </span>
  );
}

export function LicenseStatusBadge({ status }) {
  return status === 'active'
    ? <Badge icon={CheckCircle2} className="bg-emerald-50 text-emerald-700 ring-emerald-200">Active</Badge>
    : <Badge icon={CircleSlash} className="bg-slate-100 text-slate-600 ring-slate-200">Revoked</Badge>;
}

export function ConfidenceBadge({ confidence }) {
  return confidence === 'likely'
    ? <Badge icon={Copy} className="bg-amber-50 text-amber-800 ring-amber-200">Likely duplicate</Badge>
    : <Badge icon={Copy} className="bg-slate-100 text-slate-700 ring-slate-300">Possible duplicate</Badge>;
}

// The review flags on a license row, most urgent first.
export function LicenseFlags({ license }) {
  if (license.status !== 'active') return <LicenseStatusBadge status={license.status} />;
  const likely = (license.duplicates || []).some((d) => d.confidence === 'likely');
  const dupCount = (license.duplicates || []).length;
  const flags = [];
  if (license.blocked) {
    flags.push(
      <Badge key="blocked" icon={Ban} className="bg-rose-50 text-rose-700 ring-rose-200"
        title={`Last refused ${fmtDateTime(license.last_refused_at)}; nothing has changed on the license since`}>
        Locked out
      </Badge>,
    );
  }
  if (dupCount > 0) {
    flags.push(
      <span key="dup" className="inline-flex items-center gap-1">
        <ConfidenceBadge confidence={likely ? 'likely' : 'possible'} />
        {dupCount > 1 && <span className="text-[10px] font-semibold text-slate-500">×{dupCount}</span>}
      </span>,
    );
  }
  if (license.at_limit && flags.length === 0) {
    flags.push(<Badge key="full" icon={Gauge} className="bg-slate-100 text-slate-600 ring-slate-200">At limit</Badge>);
  }
  if (flags.length === 0) return <LicenseStatusBadge status="active" />;
  return <span className="flex flex-wrap items-center gap-1">{flags}</span>;
}

// SlotMeter — active computers against the allowance, as length in the one hue.
export function SlotMeter({ active, max }) {
  const pct = max > 0 ? Math.min(100, (active / max) * 100) : 0;
  return (
    <div className="min-w-[88px]">
      <div className="text-sm font-semibold tabular-nums text-slate-700">
        {fmtInt(active)} <span className="font-normal text-slate-400">/ {fmtInt(max)}</span>
      </div>
      <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
        <div className="h-full rounded-full" style={{ width: `${Math.max(active > 0 ? 6 : 0, pct)}%`, backgroundColor: MARK_HUE }} />
      </div>
    </div>
  );
}

// ─── Charts ──────────────────────────────────────────────────────────────────

// StatTile — a headline number (never a one-bar chart).
export function StatTile({ label, value, hint, icon: Icon, loading, tone }) {
  const tones = {
    alert: 'border-rose-200 bg-rose-50/60',
    warn: 'border-amber-200 bg-amber-50/60',
    accent: 'border-blue-200 bg-blue-50/60',
  };
  const iconTones = { alert: 'text-rose-600', warn: 'text-amber-600', accent: 'text-blue-700' };
  return (
    <div className={`rounded-2xl border p-4 shadow-sm ${tones[tone] || 'border-slate-200 bg-white'}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>
        {Icon && <Icon className={`h-4 w-4 shrink-0 ${iconTones[tone] || 'text-slate-300'}`} />}
      </div>
      {loading ? (
        <div className="mt-2 h-7 w-20 animate-pulse rounded bg-slate-100" />
      ) : (
        <p className="mt-1 text-2xl font-extrabold tabular-nums text-slate-800">
          {typeof value === 'number' ? fmtInt(value) : (value ?? '—')}
        </p>
      )}
      {hint && !loading && <p className="mt-0.5 text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

// fillDays turns the backend's sparse day list into one point per day of the
// window, so a quiet day reads as zero instead of disappearing from the axis.
export function fillDays(points, days) {
  const byDay = new Map((points || []).map((p) => [p.day, p.count]));
  const out = [];
  const today = new Date();
  for (let i = days - 1; i >= 0; i -= 1) {
    const d = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - i));
    const key = d.toISOString().slice(0, 10);
    out.push({ day: key, count: byDay.get(key) || 0 });
  }
  return out;
}

// TrendBars — one series over time as columns, with a per-bar tooltip.
export function TrendBars({ points, label = 'activations', emptyLabel = 'Nothing in this window', height = 120 }) {
  const [hover, setHover] = useState(null);
  const data = points || [];
  const total = data.reduce((sum, p) => sum + (p.count || 0), 0);

  if (data.length === 0 || total === 0) {
    return <p className="py-10 text-center text-sm text-slate-400">{emptyLabel}</p>;
  }
  const max = Math.max(...data.map((p) => p.count || 0), 1);

  return (
    <div>
      <div className="relative">
        {hover && (
          <div
            className="pointer-events-none absolute z-20 -translate-x-1/2 rounded-lg bg-slate-900 px-2.5 py-1.5 text-xs font-medium text-white shadow-lg"
            style={{ left: `${hover.left}%`, bottom: `${height + 8}px` }}
          >
            <span className="block whitespace-nowrap font-semibold">{fmtInt(hover.count)} {label}</span>
            <span className="block whitespace-nowrap text-slate-300">{fmtDay(hover.day)}</span>
          </div>
        )}
        {/* 2px gaps keep adjacent bars from reading as one block. */}
        <div className="flex items-end gap-[2px] border-b border-slate-200" style={{ height: `${height}px` }}>
          {data.map((p, i) => {
            const pct = ((p.count || 0) / max) * 100;
            const isHovered = hover?.index === i;
            const show = () => setHover({ ...p, index: i, left: ((i + 0.5) / data.length) * 100 });
            return (
              <button
                key={p.day}
                type="button"
                onMouseEnter={show}
                onMouseLeave={() => setHover(null)}
                onFocus={show}
                onBlur={() => setHover(null)}
                className="relative flex h-full flex-1 items-end outline-none"
                aria-label={`${fmtDay(p.day)}: ${fmtInt(p.count)} ${label}`}
              >
                {p.count > 0 && (
                  <span
                    className="w-full rounded-t transition-opacity"
                    style={{ height: `${Math.max(3, pct)}%`, backgroundColor: MARK_HUE, opacity: hover && !isHovered ? 0.45 : 1 }}
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>
      <div className="mt-2 flex items-center justify-between text-xs text-slate-400">
        <span>{fmtDay(data[0].day)}</span>
        <span className="font-medium text-slate-500">{fmtInt(total)} total</span>
        <span>{fmtDay(data[data.length - 1].day)}</span>
      </div>
    </div>
  );
}

// Pagination — shared footer for the paginated tables.
export function Pagination({ total, limit, offset, onChange, noun = 'row' }) {
  const page = Math.floor(offset / limit) + 1;
  const pageCount = Math.max(1, Math.ceil(total / limit));
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 px-4 py-3 text-sm text-slate-500">
      <span>
        {fmtInt(total)} {noun}{total === 1 ? '' : 's'} · page {page} of {pageCount}
      </span>
      <div className="flex items-center gap-2">
        <Button variant="secondary" className="px-3 py-1.5" disabled={offset === 0} onClick={() => onChange(Math.max(0, offset - limit))}>
          Previous
        </Button>
        <Button variant="secondary" className="px-3 py-1.5" disabled={page >= pageCount} onClick={() => onChange(offset + limit)}>
          Next
        </Button>
      </div>
    </div>
  );
}
